/**
@format
@flow
*/
import React from 'react';
import styled from 'styled-components';
import { Default as KrispyDefault } from '@krispy/images/web';
import { Constants } from '@ziynx/components/layout';
import { Heading } from '@ziynx/components/typography';
import Colors from '@ziynx/theme/colors';

type LayoutConfig = {
  border?: string, // 1px solid color
  borderRadius?: string // 16px
};

type LinkType = {
  href?: string,
  target?: string,
  onClick?: () => void,
}

type Props = $ReadOnly<{|
  /**
   * If true, Image will function as a background image instead of standalone.
   */
  isBackground?: boolean,
  /**
   * URL for image to be used.
   */
  src: string,
  /**
   * Alt text for image.
   */
  alt?: string,
  /**
   * Set image width.
   */
  width?: string,
  /**
   * Set image height.
   */
  height?: string,
  /**
   * Link config object when image is empty
   */
  link?: LinkType,
  /**
   * Custom config object for layout
   * { border: 1px solid color, borderRadius: 2px }
   * Is passed, will render border/borderRadius
   */
  layoutConfig?: LayoutConfig,
  /**
   * Allows use of onError native event, prevents flow issue
   */
  onError?: any,

|}>;

const EmptyImageWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: ${({ width }) => width};
  height: ${({ width }) => width};
  border-radius: ${Constants?.borderRadiusMedium}px;
  border: 2px dashed ${Colors?.GrayLight};
  box-sizing: border-box;
`;

const StyledAnchor = styled.a`
  cursor: pointer;
`;

const Image = (props: Props): React$Node => {
  const {
    src, alt, width, height, isBackground, link, layoutConfig, ...rest
  } = props;

  if (!src) {
    return (
      <StyledAnchor {...link}>
        <EmptyImageWrapper width={width}>
          <Heading medium color={Colors?.Gray}>
            No photo
          </Heading>
        </EmptyImageWrapper>
      </StyledAnchor>
    );
  }

  return (
    <KrispyDefault
      isBackground={isBackground}
      imageSource={src}
      width={width}
      height={height}
      alt={alt}
      layoutConfig={layoutConfig}
      {...rest}
    />
  );
};

Image.defaultProps = {
  isBackground: false,
  alt: 'zyinx image',
  height: 'auto',
  width: 'auto',
  layoutConfig: undefined,
};

export default Image;
